// FILE: src/components/sprint/RoadmapKanban.tsx

import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, CircleDashed, Loader } from 'lucide-react';

type TaskStatus = 'done' | 'in-progress' | 'todo' | string; // Allow for other statuses

interface KanbanTask {
  text: string;
  status: TaskStatus;
}

const getStatusAttributes = (status: TaskStatus) => {
  switch (status) {
    case 'done':
      return { Icon: CheckCircle2, color: 'text-emerald-500' };
    case 'in-progress':
      return { Icon: Loader, color: 'text-sky-500' };
    case 'todo':
    default:
      return { Icon: CircleDashed, color: 'text-slate-400' };
  }
};

const KanbanCard: React.FC<{ task: KanbanTask; index: number }> = ({ task, index }) => {
  const { Icon, color } = getStatusAttributes(task.status);
  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      whileInView={{ opacity: 1, x: 0 }}
      viewport={{ once: true }}
      transition={{ delay: 0.3 + index * 0.05, ease: 'easeOut' }}
      className="bg-white p-3 rounded-lg border-2 border-black flex items-start gap-3"
    >
      <Icon size={18} className={`mt-0.5 shrink-0 ${color} ${task.status === 'in-progress' ? 'animate-spin' : ''}`} />
      <span className="text-sm text-slate-800">{task.text}</span>
    </motion.div>
  );
};

const KanbanColumn: React.FC<{ title: string; icon: React.ReactNode; tasks: KanbanTask[]; index: number }> = ({ title, icon, tasks, index }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ delay: index * 0.1, duration: 0.5, ease: 'easeOut' }}
      className="bg-slate-100 p-4 rounded-xl border-2 border-slate-300 flex-1 min-w-[320px] h-fit"
    >
      <div className="flex items-center gap-3 mb-4 p-2">
        <span className="p-2 bg-white border-2 border-black rounded-md">
            {icon}
        </span>
        <h3 className="font-bold text-lg text-gray-900">{title}</h3>
      </div>
      <div className="space-y-3">
        {tasks.map((task, i) => (
          <KanbanCard key={i} task={task} index={i} />
        ))}
      </div>
    </motion.div>
  );
};

// The props are simplified to accept pre-processed data.
export interface KanbanColumnData {
    title: string;
    icon: React.ReactNode;
    tasks: KanbanTask[];
}

interface RoadmapKanbanProps {
  columns: KanbanColumnData[];
}

export const RoadmapKanban: React.FC<RoadmapKanbanProps> = ({ columns }) => {
  return (
    <div className="flex gap-6 overflow-x-auto pb-6 -mx-4 px-4 custom-scrollbar">
      {columns.map((col, i) => (
        <KanbanColumn
          key={col.title}
          index={i}
          title={col.title}
          icon={col.icon}
          tasks={col.tasks}
        />
      ))}
    </div>
  );
};